import React, { useState, KeyboardEvent, ChangeEvent, ClipboardEvent } from 'react';
import { BookModel } from '../../../interfaces/book.interface';
import { Toast } from '../../../components/Common/Toast';
import { sendShareLinkToEmail } from '../services/BookFlip.service';
import { faLink, faPaperPlane, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '../../../components/Common/Loader';

interface FlipBookShareProps {
  bookData: BookModel;
  close?: () => void;
  inputAsButton?: boolean;
  isCentered?: boolean;
  isInline?: boolean;
}

const FlipBookShare: React.FC<FlipBookShareProps> = ({
                                                       bookData,
                                                       close,
                                                       inputAsButton,
                                                       isCentered,
                                                       isInline,
                                                     }: FlipBookShareProps) => {
  const [loader, setLoader] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [currentEmail, setCurrentEmail] = useState('');

  const handleCopy = () => {
    navigator.clipboard.writeText(bookData?.shareUrl || '').then(() => {
      Toast('Link copied successfully', false);
    });
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const processEmailString = (emailString: string) => {
    const emails = emailString
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    emails.forEach(email => {
      if (validateEmail(email) && !emailList?.includes(email)) {
        setEmailList(prev => [...prev, email]);
        setCurrentEmail('');
      } else if (emailList?.includes(email)) {
        Toast('Email already added: ' + email, true);
      } else {
        Toast('Invalid email address: ' + email, true);
      }
    });
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    processEmailString(pastedText);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      if (currentEmail) {
        processEmailString(currentEmail);
      }
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.endsWith(',')) {
      const emailToAdd = value.slice(0, -1);
      if (emailToAdd) {
        processEmailString(emailToAdd);
      }
      setCurrentEmail('');
    } else {
      setCurrentEmail(value);
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setEmailList(emailList.filter(email => email !== emailToRemove));
  };

  const handleSendShareLinkToEmail = async () => {
    if (loader) return;
    if (!emailList.length) {
      Toast('Please enter at least one email address', true);
      return;
    }
    setLoader(true);
    try {
      await sendShareLinkToEmail(bookData?.bookId!, emailList);
      Toast('Share link sent successfully', false);
      setEmailList([]);
    } catch (error) {
      Toast('Failed to send share link', true);
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className={`flex flex-col ${isCentered ? 'items-center' : 'items-start'}`}>
      <label htmlFor="emails" className="block mb-2 font-medium">
        Email addresses
      </label>

      <div className="w-full min-h-[84px] border border-gray-300 rounded p-2 mb-4">
        <div className={`w-full flex flex-wrap gap-1 ${isInline ? 'max-w-[400px]' : 'max-w-[500px]'}`}>
          {emailList.map((email, index) => (
            <div
              key={index}
              className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm"
            >
              <span>{email}</span>
              <button
                onClick={() => removeEmail(email)}
                className="ml-2 text-gray-500 hover:text-gray-700"
                type="button"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
          <input
            type="email"
            id="emails"
            name="emails"
            placeholder="name@buildmystory.ai or paste multiple emails"
            className="flex-1 min-w-[200px] outline-none"
            value={currentEmail}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            autoComplete="email"
          />
        </div>
      </div>

      <div className={`${isCentered && 'flex justify-center'}`}>
        <button
          onClick={handleSendShareLinkToEmail}
          className="bg-storyblueSecondLightText hover:bg-storyblueSecondLightTextBold text-white font-semibold py-2 px-4 rounded-full focus:outline-none focus:ring-1 w-auto flex items-center"
        >
          <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
          Send
          <Loader loader={loader} dimentions="w-6 h-6" />
        </button>
      </div>

      <p className="text-sm text-gray-500 mt-4 mb-2">Copy and share your link!</p>

      <div>
        <button
          onClick={handleCopy}
          className="bg-storyblueSecondLightText hover:bg-storyblueSecondLightTextBold text-white font-semibold py-2 px-4 rounded-full focus:outline-none focus:ring-1 w-auto flex items-center"
        >
          <FontAwesomeIcon icon={faLink} className="mr-2" />
          Copy Link
        </button>
      </div>
    </div>
  );
};

export default FlipBookShare;
